import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import BulkDeleteModal from "../components/BulkDeleteModal";
import Header from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDateShort } from "../lib/currency";

export default function AdminOrgs() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (type) params.type = type;
      if (status) params.status = status;
      if (q) params.q = q;
      const { data } = await api.get("/admin/orgs", { params });
      setRows(data);
      setSelected({});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setLoading(false); }
  }, [type, status, q, t]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (o) => {
    const suspend = o.status !== "suspended";
    if (!window.confirm(suspend ? t("admin_orgs.confirm_suspend", { name: o.name }) : t("admin_orgs.confirm_reactivate", { name: o.name }))) return;
    try {
      await api.post(`/admin/orgs/${o.id}/${suspend ? "suspend" : "reactivate"}`);
      toast.success(suspend ? t("admin_orgs.suspended") : t("admin_orgs.reactivated"));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    }
  };

  // Rows with Stripe LIVE sub cannot be selected. We approximate via subscription_status field
  // + we let the backend reject with reason=active_stripe_subscription; the row will re-appear in the list.
  const isLocked = (o) => o.subscription_status === "active" || o.subscription_status === "trialing";

  const selectableRows = useMemo(() => rows.filter((r) => !isLocked(r)), [rows]);
  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selectedItems = useMemo(
    () => rows.filter((r) => selected[r.id]).map((r) => ({ id: r.id, label: `${r.name} (${r.type})` })),
    [rows, selected]
  );
  const allChecked = selectableRows.length > 0 && selectedIds.length === selectableRows.length;
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else {
      const next = {};
      selectableRows.forEach((r) => { next[r.id] = true; });
      setSelected(next);
    }
  };
  const toggleOne = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const doBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/orgs/bulk-delete", { ids: selectedIds });
      const rejected = (data.errors || []).filter((e) => e.reason === "active_stripe_subscription").length;
      toast.success(t("admin_orgs.bulk_deleted_toast", {
        orgs: data.deleted_orgs ?? 0,
        users: data.deleted_users ?? 0,
        missions: data.deleted_missions ?? 0,
        consultants: data.deleted_consultants ?? 0,
      }));
      if (rejected > 0) {
        toast.error(t("admin_orgs.stripe_locked_warn", { count: rejected }));
      }
      setBulkOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-orgs-page">
      <Header />
      <Toaster richColors position="top-right" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("admin_orgs.eyebrow")}</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900" data-testid="admin-orgs-title">{t("admin_orgs.title")}</h1>
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input placeholder={t("admin_orgs.search_placeholder")} value={q} onChange={(e) => setQ(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-orgs-q" />
          <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-orgs-type">
            <option value="">{t("admin_orgs.all_types")}</option>
            <option value="employer">employer</option>
            <option value="agency">agency</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-orgs-status">
            <option value="">{t("admin_orgs.all_statuses")}</option>
            <option value="active">active</option>
            <option value="pending_approval">pending_approval</option>
            <option value="rejected">rejected</option>
            <option value="suspended">suspended</option>
          </select>
          {selectedIds.length > 0 && (
            <button
              onClick={() => setBulkOpen(true)}
              className="ml-auto rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700"
              data-testid="admin-orgs-bulk-delete-btn"
            >
              {t("admin_bulk.delete_n_items", { count: selectedIds.length })}
            </button>
          )}
        </div>
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          {loading ? <p className="text-sm text-slate-500 p-2">{t("common.loading")}</p> : rows.length === 0 ? (
            <p className="text-sm text-slate-500 p-2">{t("common.no_results")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-2 pl-1 w-8 text-left">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      data-testid="admin-orgs-select-all"
                    />
                  </th>
                  <th className="py-2 text-left">{t("admin_orgs.col_name")}</th>
                  <th className="py-2 text-left">{t("admin_orgs.col_type")}</th>
                  <th className="py-2 text-left">{t("admin_orgs.col_status")}</th>
                  <th className="py-2 text-left">{t("admin_orgs.col_sub")}</th>
                  <th className="py-2 text-left">{t("admin_orgs.col_users")}</th>
                  <th className="py-2 text-left">{t("admin_orgs.col_created")}</th>
                  <th className="py-2 text-right">{t("admin_orgs.col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => {
                  const locked = isLocked(o);
                  return (
                    <tr key={o.id} className="border-t border-slate-100" data-testid={`admin-org-row-${o.id}`}>
                      <td className="py-2 pl-1">
                        <input
                          type="checkbox"
                          checked={!!selected[o.id]}
                          disabled={locked}
                          onChange={() => toggleOne(o.id)}
                          title={locked ? t("admin_orgs.stripe_locked_tip") : ""}
                          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-40"
                          data-testid={`admin-org-select-${o.id}`}
                        />
                      </td>
                      <td className="py-2 text-slate-900">
                        {o.type === "agency" ? (
                          <Link to={`/agencies/${o.id}`} className="hover:text-indigo-700">{o.name}</Link>
                        ) : (
                          o.name
                        )}
                      </td>
                      <td className="py-2 text-slate-700">{o.type}</td>
                      <td className="py-2">
                        <span className={
                          "rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 " +
                          (o.status === "active" ? "bg-emerald-50 text-emerald-700 ring-emerald-100" :
                           o.status === "suspended" ? "bg-rose-50 text-rose-700 ring-rose-100" :
                           "bg-slate-100 text-slate-700 ring-slate-200")
                        } data-testid={`admin-org-state-${o.id}`}>{o.status}</span>
                      </td>
                      <td className="py-2 text-slate-600">
                        {o.subscription_status || "—"}
                        {locked && (
                          <span className="ml-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-100" title={t("admin_orgs.stripe_locked_tip")}>
                            {t("admin_orgs.stripe_lock_badge")}
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-slate-500">{o.user_count}</td>
                      <td className="py-2 text-slate-500">{formatDateShort(o.created_at)}</td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() => toggle(o)}
                          className={
                            "rounded border px-2 py-0.5 text-xs font-semibold hover:bg-slate-50 " +
                            (o.status === "suspended" ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-rose-200 text-rose-700 hover:bg-rose-50")
                          }
                          data-testid={`admin-org-toggle-${o.id}`}
                        >
                          {o.status === "suspended" ? t("admin_orgs.reactivate") : t("admin_orgs.suspend")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

        <BulkDeleteModal
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onConfirm={doBulkDelete}
          busy={bulkBusy}
          items={selectedItems}
          kind="orgs"
          warning={t("admin_bulk.warning_orgs")}
        />
      </main>
    </div>
  );
}
